/**
 * Persistence for voice backend selection (Classic STT/TTS vs Gemini Live vs OpenAI Realtime).
 *
 * Also exposes `isWebSpeechSttUnreliable()` so the Classic engine can decide, at runtime,
 * to capture speech with a server STT (Deepgram) instead of the browser Web Speech API on
 * browsers where Web Speech is broken (Microsoft Edge). The Classic backend stays selected;
 * only its STT source changes. See `components/voice/useVoiceConversationEngine.js`.
 */

/** Default localStorage key for SC2 and other features (not SC3). */
export const VOICE_BACKEND_STORAGE_DEFAULT_KEY = "hd_voice_conversation_backend";

/** localStorage key for Symptom Checker 3 only (does not affect SC2). */
export const SC3_VOICE_BACKEND_STORAGE_KEY = "hd_voice_conversation_backend_sc3";

/** Default when no prop and no localStorage (symptom checker v2 standalone + dashboard). */
export const SYMPTOM_CHECKER_DEFAULT_VOICE_BACKEND = "gemini_live";

/** Default for Symptom Checker 3 experimental voice (isolated storage key). */
export const SYMPTOM_CHECKER_SC3_DEFAULT_VOICE_BACKEND = "openai_realtime";

/** localStorage key for Symptom Checker 4 only (does not affect SC2/SC3). */
export const SC4_VOICE_BACKEND_STORAGE_KEY = "hd_voice_conversation_backend_sc4";

/** Default for Symptom Checker 4 voice (isolated storage key). */
export const SYMPTOM_CHECKER_SC4_DEFAULT_VOICE_BACKEND = "openai_realtime";

/**
 * Multilingual (80+ language) symptom-checker backend: Gemini Live Translate STT to English
 * + English brain + translated TTS back to the patient's language. Used by CareStart3.
 */
export const VOICE_BACKEND_GEMINI_LIVE_TRANSLATE = "gemini_live_translate";

/** Backends understood by the switch. Keep getStored/set/resolve and BackendSwitch in sync. */
const VALID_VOICE_BACKENDS = new Set([
  "legacy",
  "gemini_live",
  "openai_realtime",
  VOICE_BACKEND_GEMINI_LIVE_TRANSLATE,
]);

/**
 * @param {string} [storageKey]
 * @returns {'legacy'|'gemini_live'|'openai_realtime'|'gemini_live_translate'|null}
 */
export function getStoredVoiceBackend(storageKey = VOICE_BACKEND_STORAGE_DEFAULT_KEY) {
  try {
    const v = localStorage.getItem(storageKey);
    if (VALID_VOICE_BACKENDS.has(v)) return v;
  } catch (_) {
    /* private mode / SSR */
  }
  return null;
}

/**
 * @param {'legacy'|'gemini_live'|'openai_realtime'} backend
 * @param {string} [storageKey]
 */
export function setStoredVoiceBackend(backend, storageKey = VOICE_BACKEND_STORAGE_DEFAULT_KEY) {
  try {
    localStorage.setItem(storageKey, backend);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {'legacy'|'gemini_live'|'openai_realtime'|undefined|null} propBackend
 * @param {'legacy'|'gemini_live'|'openai_realtime'} [defaultBackend='legacy']
 * @param {string} [storageKey]
 * @returns {'legacy'|'gemini_live'|'openai_realtime'}
 */
export function resolveVoiceBackend(
  propBackend,
  defaultBackend = "legacy",
  storageKey = VOICE_BACKEND_STORAGE_DEFAULT_KEY
) {
  if (VALID_VOICE_BACKENDS.has(propBackend)) {
    return propBackend;
  }
  const stored = getStoredVoiceBackend(storageKey);
  if (stored) return stored;
  return defaultBackend;
}

/**
 * Whether the current browser's Web Speech API (SpeechRecognition) cannot be relied on
 * for speech-to-text. Currently this means Microsoft Edge (all variants: desktop `Edg/`,
 * Android `EdgA/`, iOS `EdgiOS/`), which advertises the API but errors at runtime
 * (`language-not-supported` / `network`) and returns no transcript.
 *
 * The Classic voice engine uses this to capture speech with a server STT (Deepgram) instead
 * of Web Speech on these browsers, so the Classic backend keeps working on Edge.
 *
 * SSR-safe: returns false when there is no navigator (e.g. server render / tests without DOM).
 *
 * @returns {boolean} true when the browser's Web Speech STT should not be used.
 */
export function isWebSpeechSttUnreliable() {
  if (typeof navigator === "undefined" || !navigator.userAgent) return false;
  // Matches Edg/, EdgA/, EdgiOS/ — every Chromium/legacy Edge build has broken Web Speech STT.
  return /\bEdg(?:e|A|iOS)?\//i.test(navigator.userAgent);
}
